import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { debounce, isEmpty } from 'lodash';
import { Spin } from 'antd';
import { axiosInstance } from '@share/utils';
import { HOTEL_RECENT_SEARCHES_LABEL, Urls } from '@share/constants';
import './style.scss';
import { IAccount,  LocationsTypes } from '@share/common-types';
import { useSelector } from 'react-redux';
import { IMapStateToProps } from '../../getaway';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
interface LocationSelectorProps {
  onLocationSelect: (location: any) => void;
  onSearchChange: (input: string) => void;
  locationValid: boolean;
  searchTerm?: any;
  showLocationHistory: boolean;
}

const LocationSelector: React.FC<LocationSelectorProps> = ({
  onLocationSelect,
  onSearchChange,
  locationValid,
  searchTerm,
  showLocationHistory,
}) => {
  const login = useSelector((state: IMapStateToProps) => state.loginStore);
  const account = login?.account as IAccount;
  const [locations, setLocations] = useState<any[]>([]);
  const [hotels, setHotels] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [airports, setAirports] = useState<any[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [inputValue, setInputValue] = useState('');
  const SEARCH_DEBOUNCE = 300;
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const recentSearchesStorage = localStorage.getItem(HOTEL_RECENT_SEARCHES_LABEL);
    const recentSearchesList = !isEmpty(recentSearchesStorage)? JSON.parse(recentSearchesStorage) : [];
    let recentSearchesLocations: any[] = [];
    if (recentSearchesList?.length) {
      const recentLocations = recentSearchesList
                                  .map((search: any) => ({ ...search.location, realType: search.location.type, type: LocationsTypes.Recents }))
                                  .filter((location: any, index: number, array: any[]) => array.findIndex(v => (v.code === location.code)) === index)
                                  .slice(0, 5);
      recentSearchesLocations = [...recentLocations];
    }
    setLocations(recentSearchesLocations);
  },[])
  const fetchLocations = async (searchTerm: string) => {
    if (!searchTerm) return;
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
  
    const controller = new AbortController();
    controllerRef.current = controller;
  
    setIsLoading(true);
  
    try {
      const response = await axiosInstance.get(`${Urls.Locations}?SearchString=${searchTerm}`, {
        signal: controller.signal,
      });
  
      const data = response.data.locations || [];
  
      setLocations(data);
      setHotels(data.filter((loc: any) => loc.type === 'Hotel'));
      setCities(data.filter((loc: any) => loc.type === 'City'));
      setAirports(data.filter((loc: any) => loc.type === 'Airport'));
      setIsDropdownOpen(true);

    // const selectedLocation = data[0]; 
    // if (!selectedLocation) return;

    // const newSearchObject = {
    //   checkIn: moment().startOf('day').toISOString(),
    //   checkOut: moment().add(1, 'day').startOf('day').toISOString(),
    //   location: selectedLocation,
    //   locationCode: selectedLocation.code,
    //   type: 'All',
    //   rooms: [{ adultsCount: 2, kids: [] }] 
    // };

    // const recentSearchesRaw = localStorage.getItem(HOTEL_RECENT_SEARCHES_LABEL);
    // const recentSearches = recentSearchesRaw ? JSON.parse(recentSearchesRaw) : [];

    // const currentSearchStr = JSON.stringify(newSearchObject);

    // const isAlreadyPresent = recentSearches
    //   .map((s: any) => JSON.stringify(s))
    //   .includes(currentSearchStr);

    // if (!isAlreadyPresent) {
    //   const updatedSearches = [newSearchObject, ...recentSearches].slice(0, 8);
    //   localStorage.setItem(HOTEL_RECENT_SEARCHES_LABEL, JSON.stringify(updatedSearches));
    // }

    } catch (error: any) {
      if (axios.isCancel(error) || error.name === 'CanceledError') {
      } else {
        console.error('Failed to fetch locations:', error);
        setLocations([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedFetch = useCallback(
    debounce((value: string) => {
      fetchLocations(value);
      onSearchChange(value);
    }, SEARCH_DEBOUNCE),
    [] 
  );
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    const value = event.target.value;
    setInputValue(value);
    setSelectedLocation(value);
    const recentSearchesStorage = localStorage.getItem(HOTEL_RECENT_SEARCHES_LABEL);
    const recentSearchesList = !isEmpty(recentSearchesStorage)? JSON.parse(recentSearchesStorage) : [];
    let recentSearchesLocations: any[] = [];
    if (recentSearchesList?.length) {
      const recentLocations = recentSearchesList
                                  .map((search: any) => ({ ...search.location, realType: search.location.type, type: LocationsTypes.Recents }))
                                  .filter((location: any, index: number, array: any[]) => array.findIndex(v => (v.code === location.code)) === index)
                                  .slice(0, 5);
      recentSearchesLocations = [...recentLocations];
    }
    if (value.length > 2) {
      debouncedFetch(value);
    }
    if (value.length <= 1) {
      setLocations(recentSearchesLocations);
    }
  };

  const handleFocus = () => {
    setTimeout(() => setIsDropdownOpen(true), 200);
  };
  const handleConsole = () => {
    setIsDropdownOpen(true);
    // setIsRecentSearchDropdownOpen(true);
  };

  const handleBlur = () => {
    setTimeout(() => setIsDropdownOpen(false), 200);
  };

  const handleOptionClick = (location: any) => {
    onLocationSelect(location);
    setSelectedLocation(location.name);
    setIsDropdownOpen(false);
  };

  const highlightMatch = (text: string, searchTerm: string | null | undefined) => {
    if (!searchTerm || typeof searchTerm !== "string") return text; 
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
  
    return text?.split(regex).map((part, index) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? <mark key={index}>{part}</mark> : part
    );
  };
  const color: any = account?.searchIconsColor;
  const styleColor = !isEmpty(color) ? { color } : {};  
  const intl = useIntl();
  return (
    <div
      className={`form-control-border align-items-center form-control-bg-light form-fs-md d-flex h-100 ${
        locationValid ? 'form-control-bg-light' : 'form-control-bg-light-danger'
      }`}
    >
      {/* <div className='icon-lg rounded-circle flex-shrink-0 bg-primary bg-opacity-10 text-primary mb-0 me-3'>
      <i className="bi bi-geo-alt fa-fw"></i>
      </div> */}
      <div className="w-100 hotelLocation h-100">
        <div
          className={`hotelLocation_wrap h-100 form-control-border m-0 ${
            !locationValid ? 'hotelLocation_wrap_validation' : ''
          }`}
        >
          <div className="hotelLocation_left" style={styleColor}>
            <svg
              aria-hidden="true"
              focusable="false"
              data-prefix="fas"
              data-icon="location-dot"
              className="svg-inline--fa fa-location-dot "
              role="img"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 384 512"
            >
              <path
                fill="currentColor"
                d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"
              ></path>
            </svg>
          </div>
          <div className="hotelLocation_right">
            <label className="hotelLocation_right_label"><FormattedMessage id="location" /></label>
            <div className="hotelLocation_right_input">
              <input
                type="text"
                className=""
                value={selectedLocation}
                onChange={handleSearchChange}
                placeholder={intl.formatMessage({ id: "dashboard.search.locations" })}
                onBlur={handleBlur}
                onFocus={handleFocus}
                onClick={()=>handleConsole()}
              />
            </div>
          </div>
          {isLoading && <Spin size="small" style={{ position: 'absolute', right: 10, top: 30 }} />}
          {isDropdownOpen && (
              <div className="form-control-border form-control-bg-light custom-dropdown absolute">
                <div className="dropdown-container bg-white shadow-md rounded-lg p-2 w-full max-w-md">
                {isLoading && <div className='d-flex errorMsg justify-content-center'>
                  <span><FormattedMessage id="searching" /></span>
                </div>}
                {locations?.length <= 0 && isDropdownOpen && !isLoading && <>
                  <div className='d-flex align-items-center errorMsg justify-content-center'>
                  <span><FormattedMessage id="nothing.found" /></span>  
                </div>
                </>}
                  {inputValue.length <= 2 && locations.length > 0 && !isLoading && (
                    <>
                      <div className="p-2 fw-semibold title my-2"><FormattedMessage id="recents" /></div>
                      {locations.map((location, index) => (
                        <div
                          key={index}
                          className="dropdown-item p-2 cursor-pointer dropdown-item-style"
                          onClick={() => handleOptionClick(location)}
                        >
                          <div className="d-flex align-items-center">
                            <em className="bi bi-airplane-fill me-1"></em>
                            <span className="text-gray-700 font-medium">
                              {highlightMatch(location.name, inputValue)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </>
                  )}

                  {inputValue !== '' && inputValue.length >= 3 && locations.length > 2 && !isLoading && (
                    <>
                      <div className="p-2 fw-semibold title my-2">DESTINATIONS</div>
                      {cities.map((city, index) => (
                        <div
                          key={index}
                          className="dropdown-item p-2 cursor-pointer dropdown-item-style"
                          onClick={() => handleOptionClick(city)}
                        >
                          <div className="d-flex align-items-center">
                            <em className="bi bi-geo-alt-fill me-1"></em>
                            <span className="text-gray-700 font-medium">
                              {highlightMatch(city.name, inputValue)}
                            </span>
                          </div>
                        </div>
                      ))}

                      {hotels.length > 0 && (
                        <>
                          <div className="p-2 fw-semibold title my-2">HOTELS</div>
                          {hotels.map((hotel, index) => (
                            <div
                              key={index}
                              className="dropdown-item p-2 cursor-pointer dropdown-item-style"
                              onClick={() => handleOptionClick(hotel)}
                            >
                              <div className="d-flex align-items-center">
                                <em className="bi bi-building-fill me-1"></em>
                                <span className="text-gray-700 font-medium">
                                  {highlightMatch(hotel.name, inputValue)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </>
                      )}

                      {airports.length > 0 && (
                        <>
                          <div className="p-2 fw-semibold title my-2">AIRPORTS</div>
                          {airports.map((airport, index) => (
                            <div
                              key={index}
                              className="dropdown-item p-2 cursor-pointer dropdown-item-style"
                              onClick={() => handleOptionClick(airport)}
                            >
                              <div className="d-flex align-items-center">
                                <em className="bi bi-airplane-fill me-1"></em>
                                <span className="text-gray-700 font-medium">
                                  {highlightMatch(airport.name, inputValue)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

        </div>
      </div>
    </div>
  );
};

export { LocationSelector };
